import type { ControllerSpec, ResolvedPlacement } from '@forgesworn/joystick-protocol';
import { defaultControllerLayout, resolveControllerLayout } from '@forgesworn/joystick-protocol';

export type Positions = Record<string, { x: number; y: number }>;
export type Geometry = Record<string, ResolvedPlacement>;
export function gameLayout(spec: ControllerSpec, w: number, h: number): Geometry { return resolveControllerLayout(spec,w,h) ?? defaultControllerLayout(w,h,Object.keys(spec.slots)); }
const STORAGE = 'joystick:player-layouts:v1';

/** Player number, changing answer labels and artwork are not layout identities. */
export function layoutIdentity(spec: ControllerSpec): string {
  return spec.name.split(' · P')[0] + '|' + Object.keys(spec.slots).sort().map(k => {
    const s = spec.slots[k as keyof typeof spec.slots]!;
    return `${k}:${s.mode ?? 'heading'}:${s.tapAction ?? ''}`;
  }).join(',');
}
export function layoutKey(spec: ControllerSpec, landscape: boolean): string { return `${layoutIdentity(spec)}|${landscape ? 'landscape' : 'portrait'}`; }
function records(): Record<string, Positions> {
  try {
    const raw = localStorage.getItem(STORAGE);
    if (!raw || raw.length > 262144) return {};
    const value = JSON.parse(raw);
    if (value.version !== 1 || !value.layouts || typeof value.layouts !== 'object' || Array.isArray(value.layouts)) return {};
    return value.layouts;
  } catch { return {}; }
}
export function readPositions(key: string, slots: string[]): Positions {
  const value = records()[key];
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const result: Positions = {};
  for (const k of slots) {
    const p = Object.prototype.hasOwnProperty.call(value,k) ? value[k] : undefined;
    if (p && typeof p === 'object' && Number.isFinite(p.x) && Number.isFinite(p.y) && p.x >= 0 && p.x <= 1 && p.y >= 0 && p.y <= 1) result[k] = {x:p.x,y:p.y};
  }
  return result;
}
/** A failed write leaves the editor open, rather than claiming the layout is saved. */
export function savePositions(key: string, positions: Positions): void {
  const entries = Object.entries(records()).filter(([k])=>k !== key).slice(Object.keys(positions).length ? -63 : -64);
  if (Object.keys(positions).length) entries.push([key,positions]);
  localStorage.setItem(STORAGE,JSON.stringify({version:1,layouts:Object.fromEntries(entries)}));
}
export function personalised(base: Geometry, positions: Positions, w: number, h: number, editing = false): Geometry {
  const result: Geometry = {};
  for (const [key,p] of Object.entries(base)) {
    const stick = key.startsWith('joy'), width = stick ? p.size : p.zone.width, height = stick ? p.size : p.zone.height;
    const saved = positions[key];
    const x = saved ? Math.max(width/2+4,Math.min(w-width/2-4,saved.x*w)) : p.x;
    const y = saved ? Math.max(height/2+4,Math.min(h-height/2-4,saved.y*h)) : p.y;
    result[key] = {...p,x,y,zone: saved || stick && editing ? {x:x-width/2,y:y-height/2,width,height} : {...p.zone}};
  }
  return result;
}
export function movePosition(base: Geometry, key: string, x: number, y: number, w: number, h: number): {x:number;y:number} {
  const p = base[key]!, width = key.startsWith('joy') ? p.size : p.zone.width, height = key.startsWith('joy') ? p.size : p.zone.height;
  return {x:Math.max(width/2+4,Math.min(w-width/2-4,x))/w,y:Math.max(height/2+4,Math.min(h-height/2-4,y))/h};
}
/** Compare visible hit targets; the default floating stick's larger catchment is intentional. */
export function fits(geometry: Geometry, w: number, h: number): boolean {
  const shapes = Object.entries(geometry).map(([key,p])=>({x:p.x,y:p.y,w:key.startsWith('joy') ? p.size : p.zone.width,h:key.startsWith('joy') ? p.size : p.zone.height,circle:key.startsWith('joy') || p.shape === 'circle'}));
  for (const a of shapes) if (![a.x,a.y,a.w,a.h].every(Number.isFinite) || a.x-a.w/2 < 0 || a.y-a.h/2 < 0 || a.x+a.w/2 > w+.01 || a.y+a.h/2 > h+.01) return false;
  for(let i=0;i<shapes.length;i++) for(let j=i+1;j<shapes.length;j++) {
    const a=shapes[i]!,b=shapes[j]!,dx=Math.abs(a.x-b.x),dy=Math.abs(a.y-b.y);
    if(a.circle && b.circle) { if(Math.hypot(dx,dy) < (a.w+b.w)/2-.5) return false; }
    else if(a.circle || b.circle) {
      const c=a.circle?a:b,r=a.circle?b:a;
      if(Math.hypot(Math.max(0,Math.abs(c.x-r.x)-r.w/2),Math.max(0,Math.abs(c.y-r.y)-r.h/2)) < c.w/2-.5) return false;
    } else if(dx < (a.w+b.w)/2-.5 && dy < (a.h+b.h)/2-.5) return false;
  }
  return true;
}
